import React, { useEffect } from 'react';
import { Firestore } from 'firebase/firestore';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Navigation } from './marketing/Navigation';
import { SiteHeader } from './marketing/SiteHeader';
import { PageMetaBinding } from './marketing/PageMetaBinding';
import { ALL_PAGES, LEGACY_HASH_TO_PATH } from '../data/nav';
import type { MarketingPage } from '../data/nav';
import { HomePage } from './marketing/pages/HomePage';
import { ProductsPage } from './marketing/pages/ProductsPage';
import { RecipesPage } from './marketing/pages/RecipesPage';
import { NutritionPage } from './marketing/pages/NutritionPage';
import { StoryPage } from './marketing/pages/StoryPage';
import { TeamPage } from './marketing/pages/TeamPage';
import { ContactPage } from './marketing/pages/ContactPage';
import { ClubPage } from './marketing/pages/ClubPage';
import { SiteFooter } from './marketing/SiteFooter';

interface MarketingSiteProps {
  db: Firestore | null;
}

/**
 * Pages with a full-bleed hero at the top. These keep the original
 * floating top-right hamburger and NO logo — the hero already carries the
 * brand, and the in-margin header fights the hero layout. Every other page
 * uses the SiteHeader (logo + nav aligned to the body margin).
 *
 * NOTE: this is a deliberate stopgap. There are effectively three header
 * treatments right now (home, hero pages, plain pages); a proper UI design
 * pass should unify them.
 */
const HERO_PAGES: MarketingPage[] = ['home', 'nutrition'];

/** Map a pathname back to a page id, for header choice and meta lookup. */
function pageForPath(pathname: string): MarketingPage {
  const hit = ALL_PAGES.find(
    (p) => p.path === pathname || (p.path !== '/' && pathname.startsWith(p.path + '/')),
  );
  return hit ? hit.page : 'home';
}

/**
 * Anything still pointing at an old `#hash` URL — a bookmark, a link in an
 * email, a shared message — lands here and gets sent to the real path instead
 * of silently dumping on the home page. Replaces rather than pushes, so Back
 * doesn't bounce between the two.
 */
function LegacyHashRedirect() {
  const location = useLocation();
  const hash = location.hash;

  // Recipe deep links were `#recipes/<id>`; they become `/recipes/<id>`.
  if (hash.startsWith('#recipes/')) {
    return <Navigate to={`/recipes/${hash.slice('#recipes/'.length)}`} replace />;
  }
  const mapped = LEGACY_HASH_TO_PATH[hash];
  if (mapped && mapped !== location.pathname) {
    return <Navigate to={mapped} replace />;
  }
  return null;
}

/** Scroll to top on navigation, so a new page lands at its first section
 *  rather than mid-scroll where the last one left off. */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [pathname]);
  return null;
}

/**
 * Marketing site shell. Real paths since 2026-08-30 (previously `#hash`
 * routing, which left the whole site as a single indexable URL).
 * firebase.json rewrites `**` to /index.html, so deep paths survive a refresh.
 */
export function MarketingSite({ db }: MarketingSiteProps) {
  const location = useLocation();
  const page = pageForPath(location.pathname);

  // Fade-in animation observer. Re-runs on page change so the new page's
  // .fade-in elements get observed.
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('visible')),
      { threshold: 0.1 },
    );
    document.querySelectorAll('.fade-in').forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [location.pathname]);

  return (
    <>
      <LegacyHashRedirect />
      <ScrollToTop />
      <PageMetaBinding page={page} />
      {HERO_PAGES.includes(page) ? <Navigation current={page} /> : <SiteHeader current={page} />}
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/products" element={<ProductsPage />} />
        <Route path="/recipes" element={<RecipesPage />} />
        <Route path="/recipes/:recipeId" element={<RecipesPage />} />
        <Route path="/nutrition" element={<NutritionPage />} />
        <Route path="/story" element={<StoryPage />} />
        <Route path="/team" element={<TeamPage />} />
        <Route path="/club" element={<ClubPage db={db} />} />
        <Route path="/contact" element={<ContactPage />} />
        {/* Unknown path — send it home rather than showing nothing. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <SiteFooter />
    </>
  );
}
